import SearchableSelectController from '../../pos_controllers/searchable_select_controller'

export default class extends SearchableSelectController {
  showDropdown() {
    if (!this.isOpen) {
      this.isOpen = true

      // Close other dropdowns in the same container (both searchable and multi-searchable)
      this.closeSiblingDropdowns()

      // Position dropdown using fixed positioning
      this.updateDropdownPosition()

      // Show dropdown immediately
      this.dropdownTarget.classList.remove('d-none')

      // Add scroll listener with requestAnimationFrame for smooth repositioning
      this.positionUpdateHandler = () => {
        if (this.positionUpdateFrame) {
          cancelAnimationFrame(this.positionUpdateFrame)
        }
        this.positionUpdateFrame = requestAnimationFrame(() => {
          this.updateDropdownPosition()
        })
      }
      window.addEventListener('scroll', this.positionUpdateHandler, true)

      // Listen for keyboard close/open to reposition
      if (window.visualViewport) {
        this.keyboardHandler = () => this.updateDropdownPosition()
        window.visualViewport.addEventListener('resize', this.keyboardHandler)
      }

      // Focus and select the search input (if it exists)
      if (this.hasSearchInputTarget) {
        setTimeout(() => {
          this.searchInputTarget.focus()
          this.searchInputTarget.select()
        }, 10)
      }

      // If we have cached options, show them immediately
      if (this.options.length > 0) {
        this.renderOptionsOptimized()
      } else {
        // Show loading state immediately and fetch options
        this.showLoading()
        this.performSearch('')
      }
    }
  }

  updateDropdownPosition() {
    if (!this.hasDisplayLabelTarget || !this.hasDropdownTarget) return

    // Check if keyboard is open (visual viewport is smaller than layout viewport)
    const isKeyboardOpen = window.visualViewport &&
      window.visualViewport.height < window.innerHeight - 100

    // Don't reposition when keyboard is open to prevent coordinate system mismatch
    if (isKeyboardOpen) return

    const rect = this.displayLabelTarget.getBoundingClientRect()
    const viewportWidth = window.innerWidth

    // Set dropdown width to match parent
    this.dropdownTarget.style.setProperty('--dropdown-width', `${rect.width}px`)

    // Position dropdown below parent
    this.dropdownTarget.style.top = `${rect.bottom}px`

    // Get dropdown width (use CSS variable or default to rect width)
    const dropdownWidth = this.dropdownTarget.offsetWidth || rect.width
    let leftPosition = rect.left

    // Check if dropdown would overflow right edge
    if (leftPosition + dropdownWidth > viewportWidth - 20) {
      // Align to right edge with padding
      leftPosition = viewportWidth - dropdownWidth - 20
    }

    // Ensure dropdown doesn't overflow left edge
    if (leftPosition < 20) {
      leftPosition = 20
    }

    this.dropdownTarget.style.left = `${leftPosition}px`
  }

  hideDropdown() {
    this.isOpen = false
    this.destroyDropdownScrollListener()

    // Remove position update listeners
    if (this.positionUpdateHandler) {
      window.removeEventListener('scroll', this.positionUpdateHandler, true)
      if (this.positionUpdateFrame) {
        cancelAnimationFrame(this.positionUpdateFrame)
        this.positionUpdateFrame = null
      }
      this.positionUpdateHandler = null
    }

    // Remove keyboard listener
    if (this.keyboardHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.keyboardHandler)
      this.keyboardHandler = null
    }

    this.dropdownTarget.classList.add('d-none')
  }

  closeSiblingDropdowns() {
    // Find parent container (values dropdown or similar)
    const container = this.element.closest('[data-select--filter-pill-target*="valuesDropdown"]') ||
      this.element.closest('.p-3') ||
      this.element.parentElement?.parentElement

    if (!container) return

    // Close other searchable-select dropdowns (exclude multi-searchable-select)
    container.querySelectorAll('[data-controller*="searchable-select"]:not([data-controller*="multi-searchable-select"])').forEach(el => {
      if (el !== this.element) {
        const ctrl = this.application.getControllerForElementAndIdentifier(el, 'select--searchable-select')
        if (ctrl?.isOpen) ctrl.hideDropdown()
      }
    })

    // Close multi-searchable-select dropdowns
    container.querySelectorAll('[data-controller*="multi-searchable-select"]').forEach(el => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, 'select--multi-searchable-select')
      if (ctrl?.isOpen) ctrl.hideDropdown()
    })
  }

  disconnect() {
    // Clean up position update listeners
    if (this.positionUpdateHandler) {
      window.removeEventListener('scroll', this.positionUpdateHandler, true)
      if (this.positionUpdateFrame) {
        cancelAnimationFrame(this.positionUpdateFrame)
        this.positionUpdateFrame = null
      }
      this.positionUpdateHandler = null
    }

    // Clean up keyboard listener
    if (this.keyboardHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.keyboardHandler)
      this.keyboardHandler = null
    }

    super.disconnect()
  }

  showLoading() {
    this.loadingTarget.classList.remove('d-none')
    this.hideNoResults()
  }

  hideLoading() {
    this.loadingTarget.classList.add('d-none')
  }

  showNoResults() {
    this.noResultsTarget.classList.remove('d-none')
    this.optionsContainerTarget.innerHTML = ''
  }

  hideNoResults() {
    this.noResultsTarget.classList.add('d-none')
  }

  updateDisplayLabel() {
    if (this.selectedLabel) {
      this.labelTextTarget.textContent = this.selectedLabel
      this.displayLabelTarget.classList.remove('text-muted')
    } else {
      this.labelTextTarget.textContent = this.placeholder
      this.displayLabelTarget.classList.add('text-muted')
    }
  }

  generateOptionsHtml() {
    const hasGroups = this.options.some(option => option && option.group)

    const renderOption = (option) => {
      const value = option[this.valueField] || option.id || ''
      const label = option[this.labelField] || option.text || option.name || value || 'Unknown'
      const isSelected = this.selectedValue && this.selectedValue == value

      const baseClasses = "d-flex align-items-center px-3 py-2"
      const selectedClasses = isSelected
        ? "bg-light border-left border-primary font-weight-medium"
        : ""
      const borderStyle = isSelected ? 'border-left-width: 3px;' : ''

      return `
        <div class="${baseClasses} ${selectedClasses}"
             style="cursor: pointer; transition: all 0.15s ease-in-out; ${borderStyle}"
             onmouseover="if(!this.classList.contains('bg-light')) { this.style.backgroundColor='#f8f9fa'; }"
             onmouseout="if(!this.classList.contains('bg-light')) { this.style.backgroundColor=''; }"
             data-action="click->select--searchable-select#selectOption"
             data-value="${value || ''}"
             data-label="${label || ''}">
          <span class="flex-grow-1">${label}</span>
          ${isSelected ? '<span class="text-primary ml-2"><i class="fas fa-check"></i></span>' : ''}
        </div>
      `
    }

    if (!hasGroups) {
      return this.options.map(renderOption).join('')
    }

    // Preserve group ordering based on first appearance in the options array
    const grouped = new Map()
    this.options.forEach(option => {
      const groupName = option.group || ''
      if (!grouped.has(groupName)) grouped.set(groupName, [])
      grouped.get(groupName).push(option)
    })

    let html = ''
    grouped.forEach((options, groupName) => {
      if (groupName) {
        html += `
          <div class="px-3 py-2 small text-muted font-weight-bold border-top"
               style="background-color: #f8f9fa;">
            ${groupName}
          </div>
        `
      }

      html += options.map(renderOption).join('')
    })

    return html
  }

  showError(message) {
    this.optionsContainerTarget.innerHTML = `
      <div class="px-3 py-2 small text-danger">
        ${message}
      </div>
    `
  }

  resetDependentDropdowns() {
    const wrapper = this.element.closest('[data-select-key]')
    const currentKey = wrapper ? wrapper.getAttribute('data-select-key') : ''
    if (!currentKey) return

    const prefixes = ['make_id', 'model_id', 'year', 'sub_model_id']
    let prefix = null
    let suffix = ''
    for (const p of prefixes) {
      if (currentKey.startsWith(p)) {
        prefix = p
        suffix = currentKey.slice(p.length)
        break
      }
    }
    if (!prefix) return

    this.notifyAdvancedFilterController('start-reset')

    const dependencyChain = {
      'make_id': ['model_id', 'year', 'sub_model_id'],
      'model_id': ['year', 'sub_model_id'],
      'year': ['sub_model_id']
    }

    const dependentKeys = dependencyChain[prefix]
    if (!dependentKeys) {
      this.notifyAdvancedFilterController('end-reset')
      return
    }

    dependentKeys.forEach(dependentKey => {
      const dependentWrapper = document.querySelector(`[data-select-key="${dependentKey}${suffix}"]`)
      if (dependentWrapper) {
        const searchableSelectElement = dependentWrapper.querySelector('[data-controller*="select--searchable-select"]')
        if (searchableSelectElement) {
          const hiddenSelect = searchableSelectElement.querySelector('select')
          const labelText = searchableSelectElement.querySelector('[data-select--searchable-select-target="labelText"]')

          if (hiddenSelect) {
            hiddenSelect.innerHTML = ''
            hiddenSelect.value = ''
            hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }))
          }

          if (labelText) {
            const placeholder = searchableSelectElement.dataset.placeholder || 'Type to search...'
            labelText.textContent = placeholder
            searchableSelectElement.querySelector('[data-select--searchable-select-target="displayLabel"]')?.classList.add('text-muted')
          }

          const optionsContainer = searchableSelectElement.querySelector('[data-select--searchable-select-target="optionsContainer"]')
          if (optionsContainer) {
            optionsContainer.innerHTML = ''
          }
        }
      }
    })

    setTimeout(() => {
      this.notifyAdvancedFilterController('end-reset')
    }, 50)
  }

  // Notify the advanced filter controller about dependent reset state
  notifyAdvancedFilterController(action) {
    // Try both POS and BackOffice controller identifiers
    const advancedFilterForm = document.querySelector('[data-controller*="products--advanced-filter"]') ||
      document.querySelector('[data-controller*="select--advanced-filter"]')
    if (advancedFilterForm) {
      const controllerIdentifier = advancedFilterForm.dataset.controller.includes('products--advanced-filter')
        ? 'products--advanced-filter'
        : 'select--advanced-filter'
      const controller = this.application.getControllerForElementAndIdentifier(advancedFilterForm, controllerIdentifier)
      if (controller) {
        if (action === 'start-reset') {
          controller.isResettingDependents = true
        } else if (action === 'end-reset') {
          controller.isResettingDependents = false
        }
      }
    }
  }
}
